import { Link } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import PostcodeQuoteForm from './PostcodeQuoteForm'

interface HeroProps {
  layout?: 'center' | 'split'
  showQuoteForm?: boolean
  content?: {
    title: string
    subtitle?: string
    description: string
    ctaText?: string
    ctaLink?: string
    secondaryCtaText?: string
    secondaryCtaLink?: string
    grantAmount?: string
    showGrantInfo?: boolean
    image?: string
    imageAlt?: string
  }
}

const Hero = ({
  layout = 'center',
  showQuoteForm = false,
  content = {
    title: 'Power Your Home with Renewable Energy',
    description: 'Transform your home with solar panels, heat pumps, and EV charging solutions. Reduce bills, increase value, and create a sustainable future.',
    ctaText: 'Get Free Quotation',
    ctaLink: '/quote',
    secondaryCtaText: 'Learn More',
    secondaryCtaLink: '#about',
    showGrantInfo: false
  }
}: HeroProps) => {
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleQuoteRequest = (postcode: string, address: string) => {
    console.debug('Quote requested for:', { postcode, address })
    // Redirect is handled by PostcodeQuoteForm component
  }

  const renderCenteredLayout = () => (
    <div className="relative z-10 container-custom text-center text-white pt-24 md:pt-20 pb-20 md:pb-16">
      <div
        className="parallax-element"
        style={{
          transform: `translateY(${scrollY * 0.2}px)`,
        }}
      >
        <h1 className="mb-6 text-white">
          {content.title.includes('Renewable Energy') ? (
            <>
              Power Your Home with
              <span className="text-primary-green block">Renewable Energy</span>
            </>
          ) : (
            content.title
          )}
        </h1>
        {content.subtitle && (
          <h2 className="text-2xl md:text-3xl mb-4 text-primary-green text-shadow">
            {content.subtitle}
          </h2>
        )}
        <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto text-shadow">
          {content.description}
        </p>
        {content.showGrantInfo && content.grantAmount && (
          <div className="mb-8 p-4 bg-primary-green bg-opacity-20 backdrop-blur-sm rounded-lg border border-primary-green border-opacity-30 max-w-md mx-auto">
            <p className="text-lg font-semibold text-primary-green">
              {content.grantAmount} Government Grant Available!
            </p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {content.ctaLink && content.ctaText && (
            <Link
              to={content.ctaLink}
              className="btn-primary text-lg px-8 py-4"
            >
              {content.ctaText}
            </Link>
          )}
          {content.secondaryCtaLink && content.secondaryCtaText && (
            <a
              href={content.secondaryCtaLink}
              className="btn-secondary text-lg px-8 py-4"
            >
              {content.secondaryCtaText}
            </a>
          )}
        </div>
      </div>
    </div>
  )

  const renderSplitLayout = () => (
    <div className="relative z-10 container-custom pt-24 md:pt-20 pb-20 md:pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[60vh] lg:min-h-[40vh] pt-12 lg:pt-20">
        {/* Left Content */}
        <div
          className="text-white space-y-6 parallax-element"
          style={{
            transform: `translateY(${scrollY * 0.2}px)`,
          }}
        >
          <h1 className="text-white mb-4">
            {content.title}
          </h1>
          {content.subtitle && (
            <h2 className="text-2xl md:text-3xl text-primary-green text-shadow mb-4">
              {content.subtitle}
            </h2>
          )}
          <p className="text-lg md:text-xl text-gray-200 text-shadow leading-relaxed">
            {content.description}
          </p>

          {content.grantAmount && (
            <div className="p-6 bg-primary-green bg-opacity-20 backdrop-blur-sm rounded-lg border border-primary-green border-opacity-30">
              <p className="text-xl font-bold text-primary-green mb-2">
                {content.grantAmount} Government Grant Available!
              </p>
              <p className="text-gray-200 text-sm">
                Reduce your installation costs with government incentives
              </p>
            </div>
          )}

          {!showQuoteForm && (
            <div className="flex flex-col sm:flex-row gap-4">
              {content.ctaLink && content.ctaText && (
                <Link
                  to={content.ctaLink}
                  className="btn-primary hover:bg-primary-green-dark hover:text-white text-lg px-8 py-4"
                >
                  {content.ctaText}
                </Link>
              )}
              {content.secondaryCtaLink && content.secondaryCtaText && (
                <a
                  href={content.secondaryCtaLink}
                  className="btn-secondary text-lg px-8 py-4"
                >
                  {content.secondaryCtaText}
                </a>
              )}
            </div>
          )}

          {/* Postcode form under text for homepage */}
          {showQuoteForm && (
            <div className="pt-4 pb-10">
              <PostcodeQuoteForm
                onQuoteRequest={handleQuoteRequest}
                className="shadow-2xl"
              />
            </div>
          )}
        </div>

        {/* Right Content - Image */}
        <div
          className="relative parallax-element"
          style={{
            transform: `translateY(${scrollY * 0.1}px)`,
          }}
        >
          {content.image ? (
            <div className="relative">
              <img
                src={content.image}
                alt={content.imageAlt || 'Hero image'}
                className="w-full h-auto rounded-lg shadow-2xl"
                loading="eager"
              />
              {/* TODO: Define shadowing <div className="absolute inset-0 bg-gradient-to-t from-black from-opacity-20 to-transparent rounded-lg"></div> */}
              <div className="absolute inset-0 bg-gradient-to-t from-opacity-20 to-transparent rounded-lg"></div>
            </div>
          ) : (
            <div className="bg-primary-green bg-opacity-20 backdrop-blur-sm rounded-lg p-8 text-center text-white">
              <p className="text-lg">Image placeholder</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const getResponsiveHeight = useCallback(() => {
    // On mobile/tablet: allow full height (80-100vh)
    // On large screens: limit to 50vh maximum
    const baseHeight = window.innerWidth >= 1024 ? Math.max(40, 50 - scrollY * 0.05) : Math.max(80, 100 - scrollY * 0.1)
    return baseHeight
  }, [scrollY])

  const [heroHeight, setHeroHeight] = useState(() => {
    // Initialize with appropriate height based on screen size
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024 ? 50 : 100
    }
    return 100 // Default for SSR
  })

  useEffect(() => {
    const updateHeight = () => {
      setHeroHeight(getResponsiveHeight())
    }

    const handleResize = () => {
      updateHeight()
    }

    updateHeight()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [scrollY, getResponsiveHeight])

  return (
    <section
      className="relative flex items-center justify-center overflow-hidden min-h-screen lg:min-h-0"
      style={{
        minHeight: typeof window !== 'undefined' && window.innerWidth >= 1024 ? `${heroHeight}vh` : undefined,
        transition: 'min-height 0.1s ease-out'
      }}
    >
      {/* Background layers */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-dark-background via-dark-plum to-dark-background"
        style={{
          transform: `translateY(${scrollY * 0.5}px)`,
        }}
      />

      <div className="absolute inset-0 bg-hero opacity-80" />

      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2349A665' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      />

      {/* Content */}
      {layout === 'split' ? renderSplitLayout() : renderCenteredLayout()}

      {/* Scroll indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce">
        <a
          href={content.secondaryCtaLink || '#about'}
          className="text-white opacity-70 hover:opacity-100 transition-opacity p-4 block"
          aria-label="Scroll to learn more"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </a>
      </div>
    </section>
  )
}

export default Hero